import React from 'react';
import PropTypes from 'prop-types';
import { isYes } from '../../utils/helpers';

export const MedicalCareAccordionItem = ({ formData }) => {
  const is2025Version = formData?.survivorsBenefitsForm2025VersionEnabled;
  const careExpenses = formData?.careExpenses || [];
  const medicalExpenses = formData?.medicalExpenses || [];
  const hasCareExpenses = careExpenses.length > 0;

  const showSpecialMonthlyPension =
    isYes(formData?.claimingMonthlySpecialPension) &&
    !isYes(formData?.claimantLivesInANursingHome);
  const showNursingHome = isYes(formData?.claimantLivesInANursingHome);

  const showResidentialCareFacility = is2025Version
    ? careExpenses.some(expense => expense?.careType === 'CARE_FACILITY')
    : hasCareExpenses;
  const showAdultDaycare = is2025Version
    ? careExpenses.some(expense => expense?.careType === 'ADULT_DAYCARE')
    : hasCareExpenses;
  const showInHomeCareAttendant = is2025Version
    ? careExpenses.some(
        expense => expense?.careType === 'IN_HOME_CARE_ATTENDANT',
      )
    : hasCareExpenses;
  const showCareExpensesProofOfPayment = hasCareExpenses;

  const showNursingHomeCareExpenses = is2025Version
    ? careExpenses.some(expense => expense?.careType === 'NURSING_HOME')
    : hasCareExpenses;

  const showMoreThanThreeCareExpenses = careExpenses.length === 3;
  const showMoreThanSixMedicalExpenses = medicalExpenses.length === 6;

  const showRequiredDocuments =
    showSpecialMonthlyPension ||
    showNursingHome ||
    showResidentialCareFacility ||
    showAdultDaycare ||
    showInHomeCareAttendant ||
    showCareExpensesProofOfPayment;

  const showMayNeedDocuments =
    showNursingHomeCareExpenses ||
    showMoreThanThreeCareExpenses ||
    showMoreThanSixMedicalExpenses;

  return (
    <va-accordion-item
      level={5}
      bordered
      header="Special circumstances regarding your medical care"
    >
      {showRequiredDocuments && (
        <>
          <p>You’ll need to submit these documents:</p>
          <ul>
            {showSpecialMonthlyPension && (
              <li className="vads-u-margin-bottom--2">
                <strong>To claim Special Monthly Pension (SMP)</strong>, you’ll
                need to submit an Examination for Housebound Status or Permanent
                Need for Regular Aid and Attendance (VA Form 21-2680)
                <span className="vads-u-display--block vads-u-margin-bottom--2">
                  <va-link
                    href="https://www.va.gov/pension/aid-attendance-housebound/apply-form-21-2680"
                    external
                    text="Apply for Aid and Attendance benefits or Housebound allowance"
                  />
                </span>
                <span className="vads-u-display--block">
                  <va-link
                    href="https://www.va.gov/find-forms/about-form-21-2680"
                    external
                    text="Get VA Form 21-2680 to download"
                  />
                </span>
              </li>
            )}
            {showNursingHome && (
              <li className="vads-u-margin-bottom--2">
                <strong>Because you’re in a nursing home</strong>, you’ll need
                to submit a Request for Nursing Home Information in Connection
                with Claim for Aid and Attendance (VA Form 21-0779)
                <span className="vads-u-display--block vads-u-margin-bottom--2">
                  <va-link
                    href="https://www.va.gov/supporting-forms-for-claims/submit-nursing-home-information-form-21-0779"
                    external
                    text="Submit request for nursing home information in connection with claim for Aid and Attendance"
                  />
                </span>
                <span className="vads-u-display--block">
                  <va-link
                    href="https://www.va.gov/find-forms/about-form-21-0779"
                    external
                    text="Get VA Form 21-0779 to download"
                  />
                </span>
              </li>
            )}
            {showResidentialCareFacility && (
              <li className="vads-u-margin-bottom--2">
                <strong>To claim residential care facility expenses</strong>,
                you’ll need to submit a Worksheet for a Residential Care, Adult
                Daycare, or Similar Facility from the PDF version of VA Form
                21P-534EZ
                <span className="vads-u-display--block">
                  <va-link
                    href="https://www.va.gov/find-forms/about-form-21p-534ez"
                    external
                    text="Get VA Form 21P-534EZ to download"
                  />
                </span>
              </li>
            )}
            {showAdultDaycare && (
              <li className="vads-u-margin-bottom--2">
                <strong>To claim adult daycare expenses</strong>, you’ll need to
                submit a Worksheet for a Residential Care, Adult Daycare, or
                Similar Facility from the PDF version of VA Form 21P-534EZ
                <span className="vads-u-display--block">
                  <va-link
                    href="https://www.va.gov/find-forms/about-form-21p-534ez"
                    external
                    text="Get VA Form 21P-534EZ to download"
                  />
                </span>
              </li>
            )}
            {showInHomeCareAttendant && (
              <li className="vads-u-margin-bottom--2">
                <strong>To claim expenses for an in-home care attendant</strong>
                , you’ll need to submit a Worksheet for In-Home Attendant from
                the PDF version of VA Form 21P-534EZ
                <span className="vads-u-display--block">
                  <va-link
                    href="https://www.va.gov/find-forms/about-form-21p-534ez"
                    external
                    text="Get VA Form 21P-534EZ to download"
                  />
                </span>
              </li>
            )}
            {showCareExpensesProofOfPayment && (
              <li>
                Proof of payment from care provided, such as canceled checks or
                bank statements
              </li>
            )}
          </ul>
        </>
      )}
      {showMayNeedDocuments && (
        <>
          <p>You may need to submit these documents:</p>
          <ul>
            {showNursingHomeCareExpenses && (
              <>
                <li className="vads-u-margin-bottom--2">
                  <strong>To claim nursing home expenses</strong>, you may need
                  to submit one of these documents.
                </li>
                <li className="vads-u-margin-bottom--2">
                  Request for Nursing Home Information in Connection with Claim
                  for Aid and Attendance (VA Form 21-0779)
                  <span className="vads-u-display--block vads-u-margin-bottom--2">
                    <va-link
                      href="https://www.va.gov/supporting-forms-for-claims/submit-nursing-home-information-form-21-0779"
                      external
                      text="Submit request for nursing home information in connection with claim for Aid and Attendance"
                    />
                  </span>
                  <span className="vads-u-display--block">
                    <va-link
                      href="https://www.va.gov/find-forms/about-form-21-0779"
                      external
                      text="Get VA Form 21-0779 to download"
                    />
                  </span>
                </li>
                <li className="vads-u-margin-bottom--2">
                  Examination for Housebound Status or Permanent Need for
                  Regular Aid and Attendance form (VA Form 21-2680)
                  <span className="vads-u-display--block vads-u-margin-bottom--2">
                    <va-link
                      href="https://www.va.gov/pension/aid-attendance-housebound/apply-form-21-2680"
                      external
                      text="Apply for Aid and Attendance benefits or Housebound allowance"
                    />
                  </span>
                  <span className="vads-u-display--block">
                    <va-link
                      href="https://www.va.gov/find-forms/about-form-21-2680"
                      external
                      text="Get VA Form 21-2680 to download"
                    />
                  </span>
                </li>
              </>
            )}
            {showMoreThanThreeCareExpenses && (
              <li className="vads-u-margin-bottom--2">
                <strong>If you have more than 3 care expenses</strong>, you’ll
                need to submit a Medical Expense Report (VA Form 21P-8416).
                <span className="vads-u-display--block vads-u-margin-bottom--2">
                  <va-link
                    href="https://www.va.gov/supporting-forms-for-claims/submit-medical-expense-report-form-21p-8416/"
                    external
                    text="Submit medical expenses to support a pension or parents’ D.I.C. claim"
                  />
                </span>
                <span className="vads-u-display--block">
                  <va-link
                    href="https://www.va.gov/find-forms/about-form-21p-8416"
                    external
                    text="Get VA Form 21P-8416 to download"
                  />
                </span>
              </li>
            )}
            {showMoreThanSixMedicalExpenses && (
              <li>
                <strong>
                  If you have more than 6 medical, last, burial, or other
                  expenses
                </strong>
                , you’ll need to submit a Medical Expense Report (VA Form
                21P-8416).
                <span className="vads-u-display--block vads-u-margin-bottom--2">
                  <va-link
                    href="https://www.va.gov/supporting-forms-for-claims/submit-medical-expense-report-form-21p-8416/"
                    external
                    text="Submit medical expenses to support a pension or parents’ D.I.C. claim"
                  />
                </span>
                <span className="vads-u-display--block">
                  <va-link
                    href="https://www.va.gov/find-forms/about-form-21p-8416"
                    external
                    text="Get VA Form 21P-8416 to download"
                  />
                </span>
              </li>
            )}
          </ul>
        </>
      )}
      <p>If you need fiduciary assistance:</p>
      <ul>
        <li className="vads-u-margin-bottom--2">
          A fiduciary is someone VA appoints if you need help handling your VA
          benefits. VA’s Fiduciary Program was established to protect
          beneficiaries who are unable to manage their financial affairs due to
          injury, disease, or age.
          <span className="vads-u-display--block">
            <va-link
              href="https://benefits.va.gov/fiduciary/"
              external
              text="Learn more about VA’s Fiduciary Program"
            />
          </span>
        </li>
        <li>
          <strong>If you need fiduciary assistance</strong>, you’ll need to
          submit an Examination for Housebound Status or Permanent Need for
          Regular Aid and Attendance (VA Form 21-2680)
          <span className="vads-u-display--block vads-u-margin-bottom--2">
            <va-link
              href="https://www.va.gov/pension/aid-attendance-housebound/apply-form-21-2680"
              external
              text="Apply for Aid and Attendance benefits or Housebound allowance"
            />
          </span>
          <span className="vads-u-display--block">
            <va-link
              href="https://www.va.gov/find-forms/about-form-21-2680"
              external
              text="Get VA Form 21-2680 to download"
            />
          </span>
        </li>
      </ul>
    </va-accordion-item>
  );
};

MedicalCareAccordionItem.propTypes = {
  formData: PropTypes.object,
};
